import { Chart, ScatterDataPoint, ChartConfiguration, ChartDataset } from "chart.js";

import { PropType, defineComponent, shallowRef } from 'vue';
import { NPopover, NSpin } from "naive-ui";

import ComponentBase from "~/components/component-base";

import { baseChartConfig } from '~/components/chart/chart-base.config';
import { greenColor } from "~/variables";
import { defaultsDeep } from "lodash";
import { Station } from "~/api/models/station.model";
import { AvoltaIndicator } from "~/configs/indicators/avolta-indicators.config";
import { $t } from "~/i18n";
import { Moment } from "moment";
import { Measure } from "~/api/models/measure.model";

export default defineComponent({

    extends: ComponentBase,

    components: {
        NPopover,
        NSpin,
    },

    props: {
        station: { type: Object as PropType<Station>, required: true },
        measures: { type: Array as PropType<Measure[]>, required: true},
        since: { type: Object as PropType<Moment>, required: true},
        before: { type: Object as PropType<Moment>, required: true},
    },

    computed: {

        timeUnit(): 'day' | 'hour' | 'minute' {

            if(!this.before) {
                return 'day';
            }

            const delta = this.before.diff(this.since);
            if(delta >  3 * 24 * 60 * 60 * 1000) {
                return 'day';
            } else if(delta >  2 * 60 * 60 * 1000) {
                return 'hour';
            } else {
                return 'minute';
            }
        },

        timeUnitDisplayFormat(): { day: string, hour: string, minute: string} {

            if(!this.before) {
                return { day: 'll', hour: 'lll',  minute: 'HH:mm'};
            }

            const delta = this.before.diff(this.since);
            if(delta >=  7 * 24 * 60 * 60 * 1000) {
                return { day: 'll', hour: 'lll',  minute: 'HH:mm'};
            } else if(delta >=  3 * 24 * 60 * 60 * 1000){
                return { day: 'lll', hour: 'lll',  minute: 'HH:mm'};
            } else {
                return { day: 'll', hour: 'lll',  minute: 'HH:mm'};
            }
        },

        datasets() {
            const station = this.station;

            if (!station || !this.measures) {
                return [];
            }

            const lelData: ScatterDataPoint[] = this.measures.map(m => ({ x: m.date * 1000, y: m?.values[AvoltaIndicator.CH4_lel] }));
            
            return [
                {
                    label: `${this.$t('indicators.ch4_lel.label')} (${this.$t('indicators.ch4_lel.unit')})`,
                    data: lelData.map(sp => ({x: sp.x , y: sp.y})),
                    borderColor: greenColor,
                    backgroundColor: greenColor,
                    barTickness: 20,
                    borderWidth: 1,
                    pointRadius: lelData && lelData.length <= 300 ? 0 : 0,
                    pointHitRadius: 10,
                    showLine: true,
                    stepped: 'after',
                    segment: {
                        borderColor: greenColor,
                        backgroundColor: greenColor,
                    }
                } as ChartDataset,
            ];
        }
    },

    data() {
        return {
            chart: null,
            config: defaultsDeep(
                {
                    type: 'line',
                    options: {
                        interaction: {
                            mode: 'index',
                            intersect: false,
                          },
                        stacked: false,
                        scales: {
                            y: {
                                // beginAtZero: true,
                                suggestedMin: -2,
                                suggestedMax: 50,
                                title: {
                                    display: true,
                                    align: 'center',
                                    text: $t('indicators.ch4_lel.unit'),
                                    color: 'white',
                                }
                            },
                        },
                    },
                },
                baseChartConfig(''),
            ) as ChartConfiguration,
            loading: false,
        };
    },

    mounted() {
        const chartElement: HTMLCanvasElement = this.$refs?.chart as HTMLCanvasElement;
        if (!chartElement || !this.since || !this.before) {
            return;
        }

        const chartConfig = this.config;
        chartConfig.data = { datasets: this.datasets};

        //@ts-ignore
        chartConfig.options.scales.x.time.unit = this.timeUnit;
        chartConfig.options.scales.x.suggestedMin = this.since.unix() * 1000;
        chartConfig.options.scales.x.suggestedMax = this.before.unix() * 1000;
        chartConfig.options.scales.x.time.displayFormats = this.timeUnitDisplayFormat;
        this.chart = shallowRef(new Chart(chartElement.getContext('2d'), chartConfig));
    },

    methods: {
        updateChart() {
            try {
                this.chart.data = { datasets: this.datasets};
                this.chart.update();
            } catch (e) {
                console.error(e);
            }
        },
    },

    watch: {
        datasets(datasets, old) {
            if(datasets === old) {
                return;
            }

            this.updateChart();
        }
    }
});